<script setup>
import { i18n } from 'boot/i18n';
import {
  HrCheckbox,
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrInfoBanner,
  HrListSelect,
  HrMultiSelect,
} from 'src/design-system';
import { computed, ref } from 'vue';

/**
 * Picks the service, or services, one approved amount covers.
 *
 * ONE SERVICE IS THE DEFAULT AND THE SHARED POOL IS THE EXCEPTION. A pool is a SINGLE amount every
 * service in it draws on together, so recording four separately-approved services as one pool, or
 * one shared figure as four entries, misstates the approved capacity in opposite directions — and
 * neither error is visible afterwards. So THE CONTROL CHANGES SHAPE rather than the meaning being
 * explained: unchecked, a single select cannot express a pool by accident.
 *
 * BOTH SELECTS FILTER (`use-input`), because a clinic can carry thirty or more treatment services.
 */
defineOptions({ name: 'AddApprovedServiceDialog' });

const props = defineProps({
  /** Treatment services the funder covers, as `{ label, value }`. */
  serviceOptions: {
    type: Array,
    default: () => [],
  },
  /** Treatment service ids already on this authorization. */
  usedServiceIds: {
    type: Array,
    default: () => [],
  },
});

/**
 * MARKED, NOT REMOVED. A funder can approve one service twice under different settings — in-home
 * twenty hours a week, in-clinic ten — so the picker says what is already used rather than refusing.
 */
const options = computed(() => props.serviceOptions.map((option) => ({
  ...option,
  ...(props.usedServiceIds.includes(option.value)
    && { caption: i18n.t('billingAuthorizations.hintAlreadyAdded') }),
})));

const emit = defineEmits(['ok']);

const dialog = ref(null);
const isShared = ref(false);
const selected = ref([]);

const singleValue = computed(() => selected.value[0] ?? null);

const canConfirm = computed(() => selected.value.length > 0);

const isEditing = ref(false);

/**
 * Opens empty to add, or on an entry's current services to change them. Sharing is DERIVED from the
 * selection rather than remembered, so the checkbox cannot disagree with the record.
 */
function show(current = null) {
  isEditing.value = current !== null;
  selected.value = [...(current ?? [])];
  isShared.value = selected.value.length > 1;
  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

const setShared = (shared) => {
  isShared.value = shared;
  // Keeps the first rather than clearing: only the "how many" part of the question changed.
  if (!shared) {
    selected.value = selected.value.slice(0, 1);
  }
};

function onSubmit() {
  if (!canConfirm.value) {
    return;
  }

  emit('ok', [...selected.value]);
  hide();
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="isEditing
      ? i18n.t('billingAuthorizations.titleEditServices')
      : i18n.t('billingAuthorizations.titleAddServices')"
    data-e2e="dialog-add-approved-service"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-list-select
        v-if="!isShared"
        class="col-12"
        :model-value="singleValue"
        :options="options"
        :label="i18n.t('billingAuthorizations.fields.service')"
        use-input
        hide-bottom-space
        data-e2e="select-approved-service"
        @update:model-value="selected = $event === null ? [] : [$event]"
      />

      <hr-multi-select
        v-else
        v-model="selected"
        class="col-12"
        :options="options"
        :label="i18n.t('billingAuthorizations.fields.services')"
        use-input
        hide-bottom-space
        data-e2e="select-approved-services"
      />

      <hr-checkbox
        class="col-12"
        :model-value="isShared"
        :label="i18n.t('billingAuthorizations.actionShareCap')"
        data-e2e="checkbox-approved-service-shared"
        @update:model-value="setShared"
      />

      <!-- Stated where the decision is made, and only once it has been made. -->
      <div
        v-if="isShared"
        class="col-12"
      >
        <hr-info-banner :message="i18n.t('billingAuthorizations.hintShareCap')" />
      </div>
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t(isEditing ? 'common.buttonSave' : 'common.buttonAdd')"
        type="submit"
        :disable="!canConfirm"
        data-e2e="btn-confirm-approved-service"
      />
    </template>
  </hr-form-dialog>
</template>
